import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import Checkbox from '../../components/Checkbox';
import Loader from '../../components/Loader';
import Icon from '../../components/Icon';
import Dropdown from '../../components/Dropdown';
import Image from '../../components/Image';
import Toast from '../../components/Toast';

import updateSelectedCalendars from './actions';
import messages from './messages';
import './styles.css';

export const SCRIPT_ID = 'google-api-script';
const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'];
const SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

export const GOOGLE_CALENDAR_LINKED_HASH = '#google-linked';

export function injectScript(onReady) {
  const initClient = () => {
    window.gapi.client.init({
      discoveryDocs: DISCOVERY_DOCS,
      clientId: process.env.GOOGLE_API_CLIENT_ID,
      scope: SCOPE,
    }).then(onReady);
  };

  const onLoad = () => {
    window.gapi.load('client:auth2', initClient);
  };

  if (!document.getElementById(SCRIPT_ID)) {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = 'https://apis.google.com/js/api.js';
    s.onload = onLoad;
    document.getElementsByTagName('head')[0].appendChild(s);
  } else {
    onLoad();
  }
}

function getDarkerBackgroundColor(hex) {
  const SHADE = 0.75;
  const value = (/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i).exec(hex);
  const r = parseInt(value[1], 16);
  const g = parseInt(value[2], 16);
  const b = parseInt(value[3], 16);
  return `rgb(${Math.round(r * SHADE)}, ${Math.round(g * SHADE)}, ${Math.round(b * SHADE)})`;
}

class GoogleCalendar extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    router: PropTypes.object,
    fromDate: PropTypes.string,
    toDate: PropTypes.string,
    onEvents: PropTypes.func.isRequired,
    selectedCalendars: PropTypes.object.isRequired,
    updateSelectedCalendars: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    const { router } = props;
    this.state = {
      loading: true,
      signedIn: false,
      calendars: [],
      googleUser: null,
      showToast: router && router.location.hash === GOOGLE_CALENDAR_LINKED_HASH,
      error: false,
    };
    injectScript(this.onScriptReady);
  }

  componentWillReceiveProps(nextProps) {
    const { selectedCalendars, fromDate, toDate } = this.props;
    const { calendars } = this.state;
    if (nextProps.fromDate !== fromDate || nextProps.toDate !== toDate || !isEqual(nextProps.selectedCalendars, selectedCalendars)) {
      this.fetchCalendarEvents(calendars, nextProps.selectedCalendars, nextProps.fromDate, nextProps.toDate);
    }
  }

  onScriptReady = () => {
    window.gapi.auth2.getAuthInstance().isSignedIn.listen(this.onSignInChanged);
    this.onSignInChanged();
  };

  onSignInChanged = () => {
    const authInstance = window.gapi.auth2.getAuthInstance();
    if (authInstance.isSignedIn.get()) {
      const profile = authInstance.currentUser.get().getBasicProfile();
      this.setState({
        loading: true,
        signedIn: true,
        googleUser: {
          name: profile.getName(),
          email: profile.getEmail(),
          image: profile.getImageUrl(),
        },
      });
      this.listCalendars();
    } else {
      this.setState({
        showToast: true,
        signedIn: false,
        loading: false,
      });
    }
  };

  onError = () => {
    this.disconnect();
    this.setState({
      error: true,
    });
  }

  listCalendars() {
    window.gapi.client.calendar.calendarList.list({}).then((response) => {
      const { selectedCalendars, fromDate, toDate } = this.props;
      const calendars = response.result.items;
      this.setState({ calendars, loading: false, error: false });
      this.fetchCalendarEvents(calendars, selectedCalendars, fromDate, toDate);
    }, this.onError);
  }

  fetchCalendarEvents(calendars, selectedCalendars, fromDate, toDate) {
    const batch = window.gapi.client.newBatch();
    let hasCalendars = false;

    calendars.forEach((calendar) => {
      if (selectedCalendars[calendar.id]) {
        hasCalendars = true;
        batch.add(window.gapi.client.calendar.events.list({
          calendarId: calendar.id,
          timeMin: fromDate,
          timeMax: toDate,
          showDeleted: false,
          singleEvents: true,
          orderBy: 'startTime',
        }), { id: calendar.id });
      }
    });

    const events = [];
    if (!hasCalendars) {
      this.props.onEvents(events);
      this.setState({ loading: false, error: false });
    } else {
      this.setState({ loading: true });
      batch.then(({ result }) => {
        calendars.forEach((calendar) => {
          get(result[calendar.id], 'result.items', []).forEach((item) => {
            events.push({
              ...item,
              backgroundColor: calendar.backgroundColor,
              borderColor: getDarkerBackgroundColor(calendar.backgroundColor),
            });
          });
        });
        this.props.onEvents(events);
        this.setState({ loading: false, error: false });
      });
    }
  }

  signIn = () => {
    window.gapi.auth2.getAuthInstance().signIn();
  };

  disconnect = () => {
    const authInstance = window.gapi.auth2.getAuthInstance();
    this.setState({ loading: true });
    authInstance.disconnect();
    authInstance.signOut().then(() => {
      this.setState({
        signedIn: false,
        loading: false,
        calendars: [],
        googleUser: null,
      });
      this.props.updateSelectedCalendars({});
      this.props.onEvents([]);
    });
  }

  changeSelectedStatus = (calendarId) => {
    const { selectedCalendars } = this.props;
    const newSelectedCalendars = { ...selectedCalendars };
    newSelectedCalendars[calendarId] = !newSelectedCalendars[calendarId];
    this.handleSelectedStatusChange(newSelectedCalendars);
  };

  hideAllCalendars = () => {
    const _selectedCalendars = {};
    this.state.calendars.forEach((calendar) => { _selectedCalendars[calendar.id] = false; });
    this.handleSelectedStatusChange(_selectedCalendars);
    this.props.onEvents([]);
  };

  handleSelectedStatusChange = (selectedCalendars) => {
    if (this.state.showToast) {
      this.setState({ showToast: false });
    }
    this.props.updateSelectedCalendars(selectedCalendars);
  };

  render() {
    const { error, loading, calendars, signedIn, googleUser, showToast } = this.state;
    const { intl, selectedCalendars } = this.props;

    if (error) {
      return (
        <div styleName="container">
          <div><FormattedMessage {...messages.linkError} /></div>
          <br />
          {/* eslint-disable-next-line max-len */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
          <a tabIndex={0} onClick={this.signIn} styleName="link">
            <FormattedMessage {...messages.logBack} />
          </a>
        </div>
      );
    }

    if (loading) {
      return <Loader active inline="centered" />;
    } else if (signedIn) {
      return (
        <div styleName="container">
          <Image alt={googleUser.name} src={googleUser.image} avatar />
          <span>{googleUser.email}</span>
          <Dropdown icon={<Icon link name="setting" size="large" />} styleName="float-right">
            <Dropdown.Menu styleName="settings">
              <Dropdown.Item
                text={intl.formatMessage(messages.hideAllCalendars)}
                onClick={this.hideAllCalendars}
              />
              <Dropdown.Item
                text={intl.formatMessage(messages.disconnectGoogleAccount)}
                onClick={this.disconnect}
              />
            </Dropdown.Menu>
          </Dropdown>
          {calendars.map((calendar, index) => (
            <div key={calendar.id} styleName="calendar">
              <style>
                {`#gcalendar${index} input:checked ~ label::before {
                  background: ${calendar.backgroundColor} !important;
                }`}
              </style>
              <Checkbox
                id={`gcalendar${index}`}
                label={{
                  htmlFor: `gcalendar${index}`,
                  children: googleUser.email === calendar.summary ? intl.formatMessage(messages.personal) : calendar.summary,
                }}
                checked={!!selectedCalendars[calendar.id]}
                onChange={() => this.changeSelectedStatus(calendar.id)}
                styleName="checkbox"
              />
            </div>
          ))}
          <Toast
            active={showToast}
            onDeactivate={() => this.setState({ showToast: false })}
            description={intl.formatMessage(messages.googleCalendarLinked)}
          />
        </div>
      );
    } else {
      return (
        <a tabIndex={0} onClick={this.signIn} styleName="link"> {/* eslint-disable-line max-len,jsx-a11y/anchor-is-valid,jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
          <FormattedMessage {...messages.linkGoogleCalendar} />
        </a>
      );
    }
  }
}

const mapStateToProps = (state) => ({
  selectedCalendars: state.googleCalendar.selected,
});

export default connect(mapStateToProps, { updateSelectedCalendars })(injectIntl(GoogleCalendar));
